// src/pages/EnOceanButtonManagement/index.jsx
// Admin page for managing all EnOcean buttons

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  IconButton,
  Button,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
  InputAdornment,
  Card,
  CardContent,
  Grid,
  Collapse
} from '@mui/material';
import {
  Delete as DeleteIcon,
  LinkOff as UnlinkIcon,
  RadioButtonChecked as ButtonIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Warning as WarningIcon,
  AccessTime as TimeIcon,
  SignalCellularAlt as SignalIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Info as InfoIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import enoceanService from '../../services/enoceanService';
import ButtonDiscoveryDialog from '../../components/EnOcean/ButtonDiscoveryDialog';
import { axiosInstance } from '../../services/apiClient';

// Time ago filter options
const TIME_FILTER_OPTIONS = [
  { value: 0, label: 'All buttons' },
  { value: 7, label: 'Not seen for 7+ days' },
  { value: 14, label: 'Not seen for 14+ days' },
  { value: 30, label: 'Not seen for 30+ days' },
  { value: 60, label: 'Not seen for 60+ days' },
  { value: 90, label: 'Not seen for 90+ days' },
  { value: -1, label: 'Custom...' }
];

// Format time ago
const formatTimeAgo = (dateString) => {
  if (!dateString) return 'Never';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffMonths > 0) return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
  if (diffWeeks > 0) return `${diffWeeks} week${diffWeeks > 1 ? 's' : ''} ago`;
  if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffMinutes > 0) return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
  return 'Just now';
};

// Check if button is stale (not seen for given days)
const isButtonStale = (lastSeenDate, staleDays) => {
  if (!lastSeenDate || staleDays === 0) return false;

  const date = new Date(lastSeenDate);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  return diffDays >= staleDays;
};

const EnOceanButtonManagement = () => {
  const { enqueueSnackbar } = useSnackbar();

  // Data state
  const [buttons, setButtons] = useState([]);
  const [locations, setLocations] = useState([]);
  const [persons, setPersons] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [staleFilterDays, setStaleFilterDays] = useState(0);
  const [customStaleDays, setCustomStaleDays] = useState(30);
  const [showFilters, setShowFilters] = useState(false);
  const [buttonTypeFilter, setButtonTypeFilter] = useState('all');
  const [assignmentFilter, setAssignmentFilter] = useState('all');

  // Table state
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('friendlyName');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Dialog state
  const [discoveryDialogOpen, setDiscoveryDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [buttonToDelete, setButtonToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false);
  const [buttonToUnlink, setButtonToUnlink] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedButtonDetails, setSelectedButtonDetails] = useState(null);

  // Helper to extract array from various response formats
  const extractArray = (response) => {
    if (!response) return [];
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data?.data && Array.isArray(data.data)) return data.data;
    if (data?.locations && Array.isArray(data.locations)) return data.locations;
    if (data?.persons && Array.isArray(data.persons)) return data.persons;
    return [];
  };

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      // Load buttons, locations, persons, and hubs in parallel
      const [buttonsResponse, locationsResponse, personsResponse, hubsResponse] = await Promise.all([
        enoceanService.getButtons(),
        axiosInstance.get('/locations'),
        axiosInstance.get('/persons'),
        enoceanService.getDiscoveryHubs().catch(() => ({ success: true, data: [] }))
      ]);

      setButtons(buttonsResponse?.data || []);
      setLocations(extractArray(locationsResponse));
      setPersons(extractArray(personsResponse));
      setHubs(hubsResponse?.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      enqueueSnackbar('Failed to load data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Refresh data
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    enqueueSnackbar('Data refreshed', { variant: 'success' });
  };

  // Get location name by ID
  const getLocationName = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    return location?.friendly_name || '-';
  };

  // Get person name by ID
  const getPersonName = (personId) => {
    const person = persons.find(p => p.id === personId);
    return person?.friendlyName || '-';
  };

  // Filter and sort buttons
  const filteredAndSortedButtons = useMemo(() => {
    let filtered = [...buttons];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(button =>
        button.friendlyName?.toLowerCase().includes(term) ||
        button.deviceKey?.toLowerCase().includes(term) ||
        getLocationName(button.deviceLocation)?.toLowerCase().includes(term) ||
        getPersonName(button.devicePerson)?.toLowerCase().includes(term)
      );
    }

    // Stale filter
    const effectiveStaleDays = staleFilterDays === -1 ? customStaleDays : staleFilterDays;
    if (effectiveStaleDays > 0) {
      filtered = filtered.filter(button => isButtonStale(button.lastSeen, effectiveStaleDays));
    }

    // Button type filter
    if (buttonTypeFilter !== 'all') {
      filtered = filtered.filter(button => button.buttonType === buttonTypeFilter);
    }

    // Assignment filter
    if (assignmentFilter === 'mobile') {
      filtered = filtered.filter(button => button.devicePerson);
    } else if (assignmentFilter === 'fixed') {
      filtered = filtered.filter(button => button.deviceLocation && !button.devicePerson);
    } else if (assignmentFilter === 'unassigned') {
      filtered = filtered.filter(button => !button.deviceLocation && !button.devicePerson);
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (orderBy) {
        case 'friendlyName':
          aValue = a.friendlyName || '';
          bValue = b.friendlyName || '';
          break;
        case 'deviceKey':
          aValue = a.deviceKey || '';
          bValue = b.deviceKey || '';
          break;
        case 'location':
          aValue = getLocationName(a.deviceLocation);
          bValue = getLocationName(b.deviceLocation);
          break;
        case 'person':
          aValue = getPersonName(a.devicePerson);
          bValue = getPersonName(b.devicePerson);
          break;
        case 'lastSeen':
          aValue = a.lastSeen ? new Date(a.lastSeen).getTime() : 0;
          bValue = b.lastSeen ? new Date(b.lastSeen).getTime() : 0;
          break;
        case 'buttonType':
          aValue = a.buttonType || '';
          bValue = b.buttonType || '';
          break;
        default:
          aValue = a[orderBy] || '';
          bValue = b[orderBy] || '';
      }

      if (typeof aValue === 'string') {
        return order === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return order === 'asc' ? aValue - bValue : bValue - aValue;
    });

    return filtered;
  }, [buttons, searchTerm, staleFilterDays, customStaleDays, buttonTypeFilter, assignmentFilter, order, orderBy, locations, persons]);

  // Handle sort
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Handle delete button
  const handleDeleteClick = (button) => {
    setButtonToDelete(button);
    setDeleteConfirmText('');
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmText !== 'DELETE') {
      enqueueSnackbar('Please type DELETE to confirm', { variant: 'warning' });
      return;
    }

    try {
      await enoceanService.deleteButton(buttonToDelete.id);
      enqueueSnackbar('Button deleted successfully', { variant: 'success' });
      setDeleteDialogOpen(false);
      setButtonToDelete(null);
      loadData();
    } catch (error) {
      console.error('Error deleting button:', error);
      enqueueSnackbar('Failed to delete button', { variant: 'error' });
    }
  };

  // Handle unlink button
  const handleUnlinkClick = (button) => {
    setButtonToUnlink(button);
    setUnlinkDialogOpen(true);
  };

  const handleUnlinkConfirm = async () => {
    try {
      await enoceanService.unlinkButton(buttonToUnlink.id);
      enqueueSnackbar('Button unlinked. Press the button again to re-link.', { variant: 'info' });
      setUnlinkDialogOpen(false);
      setButtonToUnlink(null);
      loadData();
    } catch (error) {
      console.error('Error unlinking button:', error);
      enqueueSnackbar('Failed to unlink button', { variant: 'error' });
    }
  };

  // Handle view details
  const handleViewDetails = async (button) => {
    setSelectedButtonDetails(button);
    setDetailsDialogOpen(true);
  };

  // Handle discover new button
  const handleDiscoverClick = () => {
    if (hubs.length === 0) {
      enqueueSnackbar('No hubs available for discovery', { variant: 'warning' });
      return;
    }
    setDiscoveryDialogOpen(true);
  };

  // Handle button registered
  const handleButtonRegistered = () => {
    loadData();
    enqueueSnackbar('Button registered successfully', { variant: 'success' });
  };

  // Statistics
  const stats = useMemo(() => {
    const total = buttons.length;
    const mobile = buttons.filter(b => b.devicePerson).length;
    const fixed = buttons.filter(b => b.deviceLocation && !b.devicePerson).length;
    const stale30Days = buttons.filter(b => isButtonStale(b.lastSeen, 30)).length;
    const neverSeen = buttons.filter(b => !b.lastSeen).length;

    return { total, mobile, fixed, stale30Days, neverSeen };
  }, [buttons]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            <ButtonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            EnOcean Button Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage all registered EnOcean buttons, discover new ones, and track their activity
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleDiscoverClick}
          >
            Discover New Button
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
              <Typography variant="h4" color="primary">{stats.total}</Typography>
              <Typography variant="caption" color="text.secondary">Total Buttons</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
              <Typography variant="h4" color="secondary">{stats.mobile}</Typography>
              <Typography variant="caption" color="text.secondary">Mobile (Person)</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
              <Typography variant="h4" color="info.main">{stats.fixed}</Typography>
              <Typography variant="caption" color="text.secondary">Fixed (Location)</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
              <Typography variant="h4" color="warning.main">{stats.stale30Days}</Typography>
              <Typography variant="caption" color="text.secondary">Stale (30+ days)</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
              <Typography variant="h4" color="error">{stats.neverSeen}</Typography>
              <Typography variant="caption" color="text.secondary">Never Seen</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search by name, hex code, location, or person..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Last Seen Filter</InputLabel>
            <Select
              value={staleFilterDays}
              onChange={(e) => setStaleFilterDays(e.target.value)}
              label="Last Seen Filter"
            >
              {TIME_FILTER_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {staleFilterDays === -1 && (
            <TextField
              size="small"
              type="number"
              label="Days"
              value={customStaleDays}
              onChange={(e) => setCustomStaleDays(parseInt(e.target.value) || 0)}
              sx={{ width: 100 }}
              inputProps={{ min: 1 }}
            />
          )}

          <Button
            size="small"
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            color={showFilters ? 'primary' : 'inherit'}
          >
            More Filters
          </Button>

          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {filteredAndSortedButtons.length} of {buttons.length} buttons
          </Typography>
        </Box>

        <Collapse in={showFilters}>
          <Box sx={{ display: 'flex', gap: 2, mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Button Type</InputLabel>
              <Select
                value={buttonTypeFilter}
                onChange={(e) => setButtonTypeFilter(e.target.value)}
                label="Button Type"
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="emergency">Emergency</MenuItem>
                <MenuItem value="action">Action</MenuItem>
                <MenuItem value="doorbell">Doorbell</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Assignment</InputLabel>
              <Select
                value={assignmentFilter}
                onChange={(e) => setAssignmentFilter(e.target.value)}
                label="Assignment"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="mobile">Mobile (Person)</MenuItem>
                <MenuItem value="fixed">Fixed (Location)</MenuItem>
                <MenuItem value="unassigned">Unassigned</MenuItem>
              </Select>
            </FormControl>

            <Button
              size="small"
              onClick={() => {
                setButtonTypeFilter('all');
                setAssignmentFilter('all');
                setStaleFilterDays(0);
                setSearchTerm('');
              }}
            >
              Clear All Filters
            </Button>
          </Box>
        </Collapse>
      </Paper>

      {/* Buttons Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'friendlyName'}
                  direction={orderBy === 'friendlyName' ? order : 'asc'}
                  onClick={() => handleSort('friendlyName')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'deviceKey'}
                  direction={orderBy === 'deviceKey' ? order : 'asc'}
                  onClick={() => handleSort('deviceKey')}
                >
                  Hex Code
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'buttonType'}
                  direction={orderBy === 'buttonType' ? order : 'asc'}
                  onClick={() => handleSort('buttonType')}
                >
                  Type
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'location'}
                  direction={orderBy === 'location' ? order : 'asc'}
                  onClick={() => handleSort('location')}
                >
                  Location
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'person'}
                  direction={orderBy === 'person' ? order : 'asc'}
                  onClick={() => handleSort('person')}
                >
                  Person
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'lastSeen'}
                  direction={orderBy === 'lastSeen' ? order : 'asc'}
                  onClick={() => handleSort('lastSeen')}
                >
                  Last Seen
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedButtons
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((button) => {
                const isStale = isButtonStale(button.lastSeen, 30);
                const neverSeen = !button.lastSeen;

                return (
                  <TableRow
                    key={button.id}
                    hover
                    sx={{
                      bgcolor: neverSeen ? 'error.lighter' : isStale ? 'warning.lighter' : 'inherit',
                      '&:hover': {
                        bgcolor: neverSeen ? 'error.light' : isStale ? 'warning.light' : 'action.hover'
                      }
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ButtonIcon fontSize="small" color="primary" />
                        <Typography variant="body2">{button.friendlyName || 'Unnamed'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {button.deviceKey}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={button.buttonType || 'unknown'}
                        color={
                          button.buttonType === 'emergency' ? 'error' :
                          button.buttonType === 'action' ? 'primary' :
                          button.buttonType === 'doorbell' ? 'info' : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {button.location?.friendlyName ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {button.location.friendlyName}
                          </Typography>
                        </Box>
                      ) : button.deviceLocation ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {getLocationName(button.deviceLocation)}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {button.person?.friendlyName ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PersonIcon fontSize="small" color="secondary" />
                          <Typography variant="body2">
                            {button.person.friendlyName}
                          </Typography>
                          <Chip size="small" label="Mobile" color="secondary" sx={{ ml: 0.5 }} />
                        </Box>
                      ) : button.devicePerson ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PersonIcon fontSize="small" color="secondary" />
                          <Typography variant="body2">
                            {getPersonName(button.devicePerson)}
                          </Typography>
                          <Chip size="small" label="Mobile" color="secondary" sx={{ ml: 0.5 }} />
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {neverSeen ? (
                          <Tooltip title="This button has never been seen">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <WarningIcon fontSize="small" color="error" />
                              <Typography variant="body2" color="error">Never</Typography>
                            </Box>
                          </Tooltip>
                        ) : isStale ? (
                          <Tooltip title="This button hasn't been seen for over 30 days">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TimeIcon fontSize="small" color="warning" />
                              <Typography variant="body2" color="warning.main">
                                {formatTimeAgo(button.lastSeen)}
                              </Typography>
                            </Box>
                          </Tooltip>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <TimeIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {formatTimeAgo(button.lastSeen)}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleViewDetails(button)}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {button.devicePerson && (
                          <Tooltip title="Unlink from Person">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleUnlinkClick(button)}
                            >
                              <UnlinkIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete Button">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(button)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            {filteredAndSortedButtons.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No buttons found matching your filters
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredAndSortedButtons.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Delete Button
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action cannot be undone. The button will be permanently removed from the system.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You are about to delete:
          </Typography>
          <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2">{buttonToDelete?.friendlyName}</Typography>
            <Typography variant="caption" color="text.secondary" fontFamily="monospace">
              {buttonToDelete?.deviceKey}
            </Typography>
          </Paper>
          <TextField
            fullWidth
            label="Type DELETE to confirm"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            sx={{ mt: 2 }}
            error={deleteConfirmText.length > 0 && deleteConfirmText !== 'DELETE'}
            helperText="Type DELETE (all caps) to confirm deletion"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={deleteConfirmText !== 'DELETE'}
          >
            Delete Button
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unlink Confirmation Dialog */}
      <Dialog open={unlinkDialogOpen} onClose={() => setUnlinkDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'warning.main' }}>
          <UnlinkIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Unlink Button
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            The button will be unlinked from its current person. To re-link, the button must be pressed again during discovery mode.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Unlinking button from:
          </Typography>
          <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2">{buttonToUnlink?.friendlyName}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
              <PersonIcon fontSize="small" color="secondary" />
              <Typography variant="body2">
                {getPersonName(buttonToUnlink?.devicePerson)}
              </Typography>
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlinkDialogOpen(false)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={handleUnlinkConfirm}
          >
            Unlink Button
          </Button>
        </DialogActions>
      </Dialog>

      {/* Button Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <ButtonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Button Details
        </DialogTitle>
        <DialogContent>
          {selectedButtonDetails && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
                <Typography variant="h6">{selectedButtonDetails.friendlyName}</Typography>
                <Typography variant="body2" color="text.secondary" fontFamily="monospace">
                  {selectedButtonDetails.deviceKey}
                </Typography>
              </Paper>

              <Divider />

              <Box>
                <Typography variant="subtitle2" gutterBottom>Type</Typography>
                <Chip
                  label={selectedButtonDetails.buttonType || 'unknown'}
                  color={
                    selectedButtonDetails.buttonType === 'emergency' ? 'error' :
                    selectedButtonDetails.buttonType === 'action' ? 'primary' :
                    selectedButtonDetails.buttonType === 'doorbell' ? 'info' : 'default'
                  }
                />
              </Box>

              {selectedButtonDetails.deviceLocation && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Location</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon color="action" />
                    <Typography variant="body2">
                      {getLocationName(selectedButtonDetails.deviceLocation)}
                    </Typography>
                  </Box>
                </Box>
              )}

              {selectedButtonDetails.devicePerson && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Assigned Person</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="secondary" />
                    <Typography variant="body2">
                      {getPersonName(selectedButtonDetails.devicePerson)}
                    </Typography>
                    <Chip size="small" label="Mobile Button" color="secondary" />
                  </Box>
                </Box>
              )}

              <Box>
                <Typography variant="subtitle2" gutterBottom>Last Seen</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TimeIcon color="action" />
                  <Typography variant="body2">
                    {selectedButtonDetails.lastSeen
                      ? `${formatTimeAgo(selectedButtonDetails.lastSeen)} (${new Date(selectedButtonDetails.lastSeen).toLocaleString()})`
                      : 'Never'
                    }
                  </Typography>
                </Box>
              </Box>

              {selectedButtonDetails.rssiDbm && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Signal Strength</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SignalIcon color="action" />
                    <Typography variant="body2">
                      {selectedButtonDetails.rssiDbm} dBm
                    </Typography>
                  </Box>
                </Box>
              )}

              <Box>
                <Typography variant="subtitle2" gutterBottom>Created</Typography>
                <Typography variant="body2">
                  {selectedButtonDetails.created_at
                    ? new Date(selectedButtonDetails.created_at).toLocaleString()
                    : '-'
                  }
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Button Discovery Dialog */}
      <ButtonDiscoveryDialog
        open={discoveryDialogOpen}
        onClose={() => setDiscoveryDialogOpen(false)}
        hubs={hubs}
        locations={locations}
        persons={persons}
        onButtonRegistered={handleButtonRegistered}
      />
    </Box>
  );
};

export default EnOceanButtonManagement;
